'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ResourceCardProps {
  resource: {
    id: number;
    title: string;
    description: string;
    type: string;
    topic: string;
    image: string;
    alt: string;
    readTime: string;
    date: string;
    isPremium: boolean;
    downloadUrl?: string;
  };
  onDownload: (id: number) => void;
  onShare: (id: number) => void;
}

const ResourceCard = ({ resource, onDownload, onShare }: ResourceCardProps) => {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Guide':
        return 'DocumentTextIcon';
      case 'Webinar':
        return 'VideoCameraIcon';
      case 'Case Study':
        return 'ChartBarIcon';
      case 'Template':
        return 'DocumentDuplicateIcon';
      case 'Article':
        return 'NewspaperIcon';
      default:
        return 'DocumentIcon';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Guide':
        return 'bg-blue-100 text-blue-700';
      case 'Webinar':
        return 'bg-purple-100 text-purple-700';
      case 'Case Study':
        return 'bg-green-100 text-green-700';
      case 'Template':
        return 'bg-orange-100 text-orange-700';
      case 'Article':
        return 'bg-pink-100 text-pink-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="bg-card rounded-lg overflow-hidden shadow-card hover:shadow-modal transition-all duration-300 group">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={resource.image}
          alt={resource.alt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {resource.isPremium && (
          <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
            <Icon name="StarIcon" size={14} variant="solid" />
            <span>Premium</span>
          </div>
        )}
        <div className={`absolute top-4 left-4 ${getTypeColor(resource.type)} px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1`}>
          <Icon name={getTypeIcon(resource.type)} size={14} variant="solid" />
          <span>{resource.type}</span>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center space-x-4 text-xs text-muted-foreground mb-3">
          <span className="flex items-center space-x-1">
            <Icon name="ClockIcon" size={14} variant="outline" />
            <span>{resource.readTime}</span>
          </span>
          <span className="flex items-center space-x-1">
            <Icon name="CalendarIcon" size={14} variant="outline" />
            <span>{resource.date}</span>
          </span>
        </div>

        <h3 className="text-lg font-semibold font-headline text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors duration-300">
          {resource.title}
        </h3>

        <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
          {resource.description}
        </p>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <span className="text-xs font-medium text-primary bg-primary/5 px-3 py-1 rounded-full">
            {resource.topic}
          </span>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onShare(resource.id)}
              className="p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/5 transition-all duration-300"
              aria-label="Share resource"
            >
              <Icon name="ShareIcon" size={18} variant="outline" />
            </button>
            <button
              onClick={() => onDownload(resource.id)}
              className="px-4 py-2 rounded-md font-semibold text-sm text-secondary-foreground bg-secondary hover:bg-secondary/90 transition-all duration-300 flex items-center space-x-2"
            >
              <Icon name="ArrowDownTrayIcon" size={16} variant="outline" />
              <span>{resource.isPremium ? 'Get Access' : 'Download'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceCard;